<template>
  <section>
    <div class="font-normal mb-4">
      <el-form
        label-position="top"
        :model="withdrawalForm"
        ref="withdrawalForm"
        :rules="rules"
        label-width="80px"
        size="normal"
      >
        <el-form-item label="Country" prop="country">
          <el-select
            class="w-full"
            filterable
            remote
            v-model="withdrawalForm.country"
            placeholder="Search Country"
            :remote-method="remoteMethod"
            :loading="loading"
          >
            <el-option
              v-for="(country, index) in options"
              :key="index"
              :label="country.name"
              :value="country.code2"
              :disabled="country.disabled"
            >
              <el-tooltip
                content="We do not offer service to this country"
                placement="top"
                :disabled="!country.disabled"
              >
                <span>
                  {{ country.name }}
                </span>
              </el-tooltip>
            </el-option>
            <i slot="prefix" class="el-input__icon el-icon-search"></i>
          </el-select>
        </el-form-item>
        <el-form-item label="Currency" prop="currency">
          <el-select
            class="w-full"
            v-model="withdrawalForm.currency"
            placeholder="Select Currency"
            @change="selectCurrency"
          >
            <div slot="prefix" class="flex items-center mr-4 mt-3">
              <currency-icons
                :currency="getCurrencyCountry(withdrawalForm.currency)"
              />
            </div>
            <el-option
              v-for="item in filteredCurrencies"
              :key="item.currency"
              :value="item.currency"
            >
              <div class="flex gap-x-3 items-center h-auto pt-2 pb-2">
                <currency-icons :currency="getCurrencyCountry(item.currency)" />
                <p class="text-sm font-bold text-root-2 capitalize">
                  {{ item.currency }}
                </p>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        <div v-if="hasSelectPaymentMethodOption">
          <el-form-item label="Select Payment Method" prop="paymentMethod">
            <el-select
              class="w-full"
              v-model="withdrawalForm.paymentMethod"
              placeholder="Select Payment Method"
              @change="selectPaymentMethod"
            >
              <el-option
                v-for="item in enabledPaymentMethods"
                :key="item.value"
                :value="item.value"
                :label="item.label"
              >
              </el-option>
            </el-select>
          </el-form-item>
          <div v-if="isMobileMoneyMethod">
            <mobile-money-withdrawal-form @withdrawalAccountHandler="next" />
          </div>
        </div>
        <div v-if="!isMobileMoneyMethod">
          <el-form-item
            v-if="!isSearchableBankDetails"
            label="Bank Name"
            prop="bankName"
          >
            <el-input
              class="w-full"
              v-model="withdrawalForm.bankName"
              placeholder="Enter Bank Name"
            ></el-input>
          </el-form-item>
          <el-form-item v-else label="Bank Name" prop="bankName">
            <el-select
              class="w-full"
              filterable
              remote
              v-model="withdrawalForm.bankName"
              placeholder="Search Bank"
              :remote-method="
                (query) => remoteMethodBanks(query, withdrawalForm.country)
              "
              :loading="loading"
              @change="changeBankName"
            >
              <el-option
                v-for="(bank, index) in bankOptions"
                :key="index"
                :label="bank.name"
                :value="bank.name"
              >
                {{ bank.name }}
              </el-option>
              <i slot="prefix" class="el-input__icon el-icon-search"></i>
            </el-select>
          </el-form-item>
          <el-form-item label="Account Number" prop="accountNumber">
            <el-input
              class="w-full"
              v-model.trim="withdrawalForm.accountNumber"
              placeholder="Enter Account number"
              @input="changeAccountNumber"
            >
            </el-input>
          </el-form-item>
          <el-form-item
            v-if="!isSearchableBankDetails"
            label="Full Name"
            prop="fullName"
          >
            <el-input
              class="w-full"
              v-model="withdrawalForm.fullName"
              placeholder="Enter full name"
            >
            </el-input>
          </el-form-item>
          <el-form-item v-else label="Full Name" prop="fullName">
            <el-input
              class="w-full"
              v-model="withdrawalForm.fullName"
              placeholder="Enter full name"
              :readonly="isFallBackAccountName"
            >
            </el-input>
          </el-form-item>
          <el-form-item
            v-if="!isAmericanDetails && isFXDetails"
            label="Swift Code"
            prop="swiftCode"
          >
            <el-input
              class="w-full"
              v-model.trim="withdrawalForm.swiftCode"
              placeholder="Enter Swift Code"
            >
            </el-input>
          </el-form-item>

          <el-form-item
            v-if="!isAmericanDetails && isFXDetails"
            label="City"
            prop="city"
          >
            <el-input
              class="w-full"
              v-model="withdrawalForm.city"
              placeholder="Enter City"
            ></el-input>
          </el-form-item>
          <el-form-item
            v-if="!isAmericanDetails && isFXDetails"
            label="Bank Address"
            prop="bankAddress"
          >
            <el-input
              class="w-full"
              v-model="withdrawalForm.bankAddress"
              placeholder="Enter Address"
            ></el-input>
          </el-form-item>
          <el-form-item
            v-if="!isAmericanDetails && isFXDetails"
            label="Postal Code"
            prop="postalCode"
          >
            <el-input
              class="w-full"
              v-model.trim="withdrawalForm.postalCode"
              placeholder="Enter Postal Code"
            ></el-input>
          </el-form-item>
          <el-form-item
            v-if="isAmericanDetails"
            label="Routing number"
            prop="routingNumber"
          >
            <el-input
              class="w-full"
              v-model.trim="withdrawalForm.routingNumber"
              placeholder="Enter Routing number "
            >
            </el-input>
          </el-form-item>
        </div>
        <div class="pb-4" v-if="!isAmericanDetails && isFXDetails">
          <p class="font-bold text-xs mt-10 mb-2 userDetails" id="userDetails">
            Intermediary Bank
          </p>
          <hr />
          <div class="italic font-normal text-xs pt-4 pb-2">
            Do you want to utilize an intermediary bank?
          </div>
          <el-checkbox
            class="pb-2"
            v-model="showIntermediaryBankDetails"
          ></el-checkbox>
          <div v-if="showIntermediaryBankDetails">
            <el-form-item
              label="Intermediary Bank Name"
              prop="intermediaryBankName"
            >
              <el-input
                class="w-full"
                v-model="withdrawalForm.intermediaryBankName"
                placeholder="Enter Intermediary Bank Name"
              ></el-input>
            </el-form-item>
            <el-form-item
              label="Intermediary Bank Reference"
              prop="intermediaryBankReference"
            >
              <el-input
                class="w-full"
                v-model="withdrawalForm.intermediaryBankReference"
                placeholder="Enter Intermediary Bank Reference"
              ></el-input>
            </el-form-item>
            <el-form-item
              label="Enter Intermediary Bank Swift Code"
              prop="intermediaryBankSwiftCode"
            >
              <el-input
                class="w-full"
                v-model.trim="withdrawalForm.intermediaryBankSwiftCode"
                placeholder="Enter Intermediary Bank Swift Code"
              ></el-input>
            </el-form-item>
            <el-form-item
              label="Intermediary Bank Address"
              prop="intermediaryBankAddress"
            >
              <el-input
                class="w-full"
                v-model="withdrawalForm.intermediaryBankAddress"
                placeholder="Enter Intermediary Bank Address"
              ></el-input>
            </el-form-item>
          </div>
        </div>
        <el-button
          v-if="!isMobileMoneyMethod"
          class="bg-primary w-full py-3.5 text-white rounded-md text-sm font-normal mt-4"
          @click="validateForm('withdrawalForm')"
          >Continue
        </el-button>
      </el-form>
    </div>
  </section>
</template>

<script>
import {
  ALL_COUNTRIES,
  CURRENCY,
  ALL_NIGERIAN_BANKS,
  NSANO_BANKS,
  PAYMENT_METHODS,
  GHS_MONEY_PROVIDER,
  XAF_MONEY_PROVIDERS,
  XOF_MONEY_PROVIDERS,
  MOBILE_MONEY_ENABLED_CURRENCIES,
  COUNTRY_SHORTNAMES
} from '@/utils/constants.js'
import {
  ValidateInput,
  GetCurrencyCountry,
  ValidateRoutingNumber,
  ValidateSwiftCode,
  ValidateText,
  ValidateSingleSpace
} from '@/utils/helpers.js'
import breakpoints from '@/utils/breakpoints.js'
import tenant from '@/mixins/tenant.js'
import localDeposit from '@/mixins/localDeposit.js'
import filteredDepositAccounts from '@/mixins/getBalance.js'
export default {
  mixins: [tenant, localDeposit, filteredDepositAccounts],
  props: {
    showDialog: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      breakpoints,
      mobileMoneyCurrencies: MOBILE_MONEY_ENABLED_CURRENCIES,
      paymentMethods: PAYMENT_METHODS,
      showIntermediaryBankDetails: false,
      allCountries: ALL_COUNTRIES,
      currency: CURRENCY,
      allBanks: ALL_NIGERIAN_BANKS,
      nsanoBanks: NSANO_BANKS,
      ghsMoneyProviders: GHS_MONEY_PROVIDER,
      loading: false,
      bankOptions: [],
      options: [],
      countries: [],
      withdrawalForm: {
        fullName: '',
        country: '',
        currency: '',
        accountNumber: '',
        city: '',
        bankName: '',
        postalCode: '',
        bankAddress: '',
        swiftCode: '',
        routingNumber: '',
        intermediaryBankReference: '',
        intermediaryBankAddress: '',
        intermediaryBankName: '',
        intermediaryBankSwiftCode: '',
        bankCode: '',
        accountName: '',
        paymentMethod: ''
      },
      rules: {
        fullName: [
          {
            required: true,
            message: 'Please input your full Name',
            trigger: ['blur', 'change']
          },
          { min: 3, message: 'Length should be at least 3 ', trigger: 'blur' },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        country: [
          {
            required: true,
            message: 'Please select a country',
            trigger: ['blur', 'change']
          }
        ],
        currency: [
          {
            required: true,
            message: 'Please select a currency',
            trigger: ['blur', 'change']
          }
        ],
        swiftCode: [
          {
            required: true,
            message: 'Please input swift code',
            trigger: ['blur', 'change']
          },
          {
            min: 8,
            max: 11,
            message: 'Length should be between 8 to 11 characters ',
            trigger: ['blur', 'change']
          },
          { validator: ValidateSwiftCode, trigger: ['blur', 'change'] }
        ],
        postalCode: [
          {
            message: 'Please input postal code',
            trigger: ['blur', 'change']
          },
          { validator: ValidateSwiftCode, trigger: ['blur', 'change'] }
        ],
        accountNumber: [
          {
            required: true,
            message: 'Please input account number',
            trigger: ['blur', 'change']
          },
          { validator: this.validateAccountNo, trigger: ['blur', 'change'] },
          { validator: ValidateSingleSpace, trigger: ['blur', 'change'] }
        ],
        intermediaryBankSwiftCode: [
          {
            required: true,
            message: 'Please input swift code',
            trigger: ['blur', 'change']
          },
          {
            min: 8,
            max: 11,
            message: 'Length should be between 8 to 11 characters ',
            trigger: ['blur', 'change']
          },
          { validator: ValidateSwiftCode, trigger: ['blur', 'change'] }
        ],
        intermediaryBankName: [
          {
            required: true,
            message: 'Please select intermediary bank name',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        intermediaryBankAddress: [
          {
            required: true,
            message: 'Please select intermediary bank address',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        intermediaryBankReference: [
          {
            required: true,
            message: 'Please select intermediary bank reference',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        bankAddress: [
          {
            required: true,
            message: 'Please enter a bank address',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        city: [
          {
            required: true,
            message: 'Please enter a bank city',
            trigger: 'blur'
          },
          { validator: ValidateText, trigger: ['blur', 'change'] }
        ],
        bankName: [
          {
            required: true,
            message: 'Please enter a bank name',
            trigger: 'blur'
          },
          { validator: ValidateInput, trigger: ['blur', 'change'] }
        ],
        paymentMethod: [
          {
            required: true,
            message: 'Please select a payment method',
            trigger: 'blur'
          }
        ],
        routingNumber: [
          {
            required: true,
            message: 'Please enter a routing number',
            trigger: 'blur'
          },
          {
            min: 9,
            max: 9,
            message: 'Length should be 9 characters ',
            trigger: ['blur', 'change']
          },
          { validator: ValidateRoutingNumber, trigger: ['blur', 'change'] }
        ]
      },
      isAccountNameError: '',
      ghanaianAccountNumberMinLength: 7,
      ghanaianAccountNumberMaxLength: 20,
      nigerianAccountNumber: 10
    }
  },
  methods: {
    next (form) {
      this.withdrawalForm = { ...this.withdrawalForm, ...form }
      this.handleWithdrawalMethodAction()
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    selectCurrency () {
      // Make mobile money the default so bank transfer fields are never shown for XOF and XAF
      this.$store.dispatch(
        'common/setSecondaryCurrency',
        this.withdrawalForm.currency
      )
      if (this.mobileMoneyCurrencies.includes(this.withdrawalForm.currency)) {
        this.withdrawalForm.paymentMethod = 'mno'
      } else {
        this.withdrawalForm.paymentMethod = this.$transactionType.BANK_TRANSFER
      }
      this.selectPaymentMethod()
    },
    selectPaymentMethod () {
      const details = {
        payoutMethod:
          this.withdrawalForm.paymentMethod === 'mno'
            ? this.$transactionType.MOBILE_MONEY
            : this.$transactionType.BANK_TRANSFER
      }
      this.$store.dispatch('terminal/setTransferDetails', details)
    },
    async validateAccountNo (rule, value, callback) {
      if (this.isFallBackAccountName) {
        if (this.computeValidAccountNumberLength(value.length)) {
          if (this.withdrawalForm.bankName) {
            // only call the endpoint if there is no account name; so validation doesn't happen every time
            if (this.isAccountNameError !== 'false') {
              await this.resolveAccountName(value)
            }
            if (this.isAccountNameError === 'true') {
              callback(new Error('Please enter correct account details'))
            }
          } else {
            callback()
          }
        } else {
          const errorMessage = this.getAccountNumberLengthErrorMessage()
          callback(new Error(errorMessage))
        }
      } else {
        callback()
      }
    },
    changeAccountNumber () {
      this.isAccountNameError = ''
    },
    remoteMethod (query) {
      if (query !== '') {
        this.loading = true
        setTimeout(() => {
          this.loading = false
          this.options = this.allCountries.filter((item) => {
            return item.name.toLowerCase().indexOf(query.toLowerCase()) > -1
          })
        }, 100)
      } else {
        this.options = []
      }
    },
    handleWithdrawalMethodAction () {
      const intermediaryDetails = this.withdrawalForm.intermediaryBankName
        ? {
            reference: this.withdrawalForm.intermediaryBankReference,
            bankAddress: this.withdrawalForm.intermediaryBankAddress,
            bankName: this.withdrawalForm.intermediaryBankName,
            swiftCode: this.withdrawalForm.intermediaryBankSwiftCode
          }
        : null
      const details = this.withdrawalForm
      delete details.intermediaryBankReference
      delete details.intermediaryBankAddress
      delete details.intermediaryBankName
      delete details.intermediaryBankSwiftCode
      details.accountNumber = details.accountNo || details.accountNumber
      details.isMobileMoney = this.isMobileMoneyMethod ? 'yes' : 'no'
      details.accountName = details.fullName || details.accountName
      details.isWithinUs = this.isAmericanDetails ? 'yes' : 'no'
      const withdrawalInfo = {
        beneficiaryDetails: {
          bankDetails: details,
          intermediaryDetails: intermediaryDetails
        }
      }
      this.$emit('closeDrawer')
      this.$emit('proceedToAddWithdrawal', withdrawalInfo)
    },
    validateForm (form) {
      this.$refs[form].validate((valid) => {
        if (valid) {
          this.handleWithdrawalMethodAction()
        } else {
          this.$notify.error({
            title: 'Invalid Details',
            message: 'Please fill the required details',
            position: 'top-right'
          })
        }
      })
    },
    handleClose () {
      this.$emit('closeDialog')
    },
    async getSupportedBanks () {
      const payload = {
        currency: this.withdrawalForm.currency,
        payoutType: this.withdrawalForm.paymentMethod === 'mno' ? 'mno' : ''
      }
      this.loading = true
      await this.$store
        .dispatch('terminal/getSupportedBanks', payload)
        .then(() => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    remoteMethodBanks (query, country) {
      if (query !== '') {
        this.loading = true
        setTimeout(() => {
          this.loading = false
          if (country === COUNTRY_SHORTNAMES.NG) {
            this.getSupportedBanks().then(() => {
              this.bankOptions = this.supportedBanks.filter((item) => {
                return item.name.toLowerCase().includes(query.toLowerCase())
              })
            })
          } else if (country === COUNTRY_SHORTNAMES.GH) {
            // filter banks for Ghana (GH)
            this.bankOptions = this.nsanoBanks.filter((item) => {
              return item.name.toLowerCase().indexOf(query.toLowerCase()) > -1
            })
          }
        }, 100)
      } else {
        this.bankOptions = []
      }
    },
    changeBankName (bankName) {
      this.withdrawalForm.bankCode = this.bankOptions.find(
        (bank) => bank.name === bankName
      ).code
      if (this.withdrawalForm.accountNo.length === 10) {
        this.resolveAccountName(this.withdrawalForm.accountNo)
      } else {
        this.withdrawalForm.accountNo = ''
      }
    },
    async resolveAccountName (accountNo) {
      this.withdrawalForm.fullName = '...'

      let selectedBank
      if (this.withdrawalForm.country === COUNTRY_SHORTNAMES.NG) {
        selectedBank = this.supportedBanks.find(
          (bank) => bank.name === this.withdrawalForm.bankName
        )
      } else if (this.withdrawalForm.country === COUNTRY_SHORTNAMES.GH) {
        selectedBank = this.nsanoBanks.find(
          (bank) => bank.name === this.withdrawalForm.bankName
        )
      }

      if (!selectedBank) {
        this.$notify.error({
          title: 'Bank name not valid',
          message: 'Please re-select from list of banks',
          position: 'top-right'
        })
        return
      }
      await this.$store
        .dispatch('terminal/setResolveAccountName', {
          account_number: accountNo,
          bank_code: selectedBank.code,
          currency: this.withdrawalForm.currency
        })
        .then(() => {
          this.isAccountNameError = 'false'
          this.withdrawalForm.fullName =
            this.$store.state.terminal.resolveAccountName
          // here, since nsano returns empty string as account nmae on dev,
          // we would allow manual account name input
          if (
            this.isOnDevEnvironment &&
            this.withdrawalForm.country === COUNTRY_SHORTNAMES.GH
          ) {
            this.isAccountNameError = 'fallback'
          }
        })
        .catch((error) => {
          this.withdrawalForm.fullName = ''
          const err = error.response
            ? error?.response?.data?.error?.publicMessage
            : error
          if (err) {
            this.$notify.error({
              title: this.httpErrorMessage.customMessage,
              message: err,
              position: 'top-right'
            })
          }
          this.isAccountNameError = 'fallback'
        })
    },
    getAccountNumberLengthErrorMessage () {
      if (this.withdrawalForm.country === COUNTRY_SHORTNAMES.GH) {
        return '7 - 20 digits required'
      } else {
        return '10 digits required'
      }
    },
    computeValidAccountNumberLength (accountNumberLength) {
      // Check if it's for Ghanaian details
      if (this.withdrawalForm.country === COUNTRY_SHORTNAMES.GH) {
        const parsedLength = parseInt(accountNumberLength)
        return (
          parsedLength >= this.ghanaianAccountNumberMinLength &&
          parsedLength <= this.ghanaianAccountNumberMaxLength
        )
      }
      // For other cases (Nigerian details)
      return parseInt(accountNumberLength) === this.nigerianAccountNumber
    }
  },
  components: {
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    MobileMoneyWithdrawalForm: () =>
      import('@/components/v2/withdrawal/layout/MobileMoneyWithdrawalForm')
  },
  computed: {
    supportedBanks () {
      return Object.values(this.$store.state.terminal.supportedBanks)[0]
    },
    strictlyMobileMoneyCurrency () {
      return [this.$CURRENCY.XOF, this.$CURRENCY.XAF].includes(
        this.withdrawalForm.currency
      )
    },
    francMoneyProviders () {
      let providers = []
      if (this.secondaryCurrencyIsXAF) {
        providers = XAF_MONEY_PROVIDERS
      } else if (this.secondaryCurrencyIsXOF) {
        providers = XOF_MONEY_PROVIDERS
      }
      return providers
    },
    enabledPaymentMethods () {
      // only display mobile money option for XOF and XAF
      let enabledMethods = []
      if (
        MOBILE_MONEY_ENABLED_CURRENCIES.includes(
          this.withdrawalForm.currency
        ) &&
        !this.withdrawalCurrencyIsGHS
      ) {
        const mobileMoneyMethod = this.paymentMethods.find(
          (method) => method.value === 'mno'
        )
        enabledMethods.push(mobileMoneyMethod)
      } else {
        enabledMethods = this.paymentMethods
      }

      return enabledMethods
    },
    isMobileMoneyMethod () {
      return this.withdrawalForm.paymentMethod === 'mno'
    },
    currencyMoneyProvider () {
      let data = []
      if (this.withdrawalCurrencyIsGHS) {
        data = this.ghsMoneyProviders
      } else {
        data = this.francMoneyProviders
      }
      return data
    },
    withdrawalCurrencyIsGHS () {
      return this.withdrawalForm.currency === this.$CURRENCY.GHS
    },
    hasSelectPaymentMethodOption () {
      return this.mobileMoneyCurrencies.includes(this.withdrawalForm.currency)
    },
    withdrawalMethods () {
      return this.$store.state.withdrawal.withdrawalMethods || []
    },
    filteredCurrencies () {
      const filteredOptions = this?.withdrawalFiatCurrencyOptions.filter(
        (option) =>
          !this?.withdrawalMethods.some(
            (method) => method?.currency === option?.currency
          )
      )
      return filteredOptions
    },
    isAmericanDetails () {
      return this.withdrawalForm.country === 'US'
    },
    isFXDetails () {
      return [
        this.$CURRENCY.USD,
        this.$CURRENCY.GBP,
        this.$CURRENCY.EUR
      ].includes(this.withdrawalForm.currency)
    },
    isSearchableBankDetails () {
      return (
        this.withdrawalForm.country === COUNTRY_SHORTNAMES.NG ||
        this.withdrawalForm.country === COUNTRY_SHORTNAMES.GH
      )
    },
    isFallBackAccountName () {
      return (
        this.isSearchableBankDetails && this.isAccountNameError !== 'fallback'
      )
    }
  },
  watch: {
    async 'withdrawalForm.accountNumber' (newValue) {
      if (this.isFallBackAccountName) {
        if (this.computeValidAccountNumberLength(newValue.length)) {
          this.recipientInfoForm.accountName = ''
        }
      }
    }
  }
}
</script>

<style scoped></style>
